/**
 * collection-filter.js
 * Zero-dependency client-side collection filtering for Shopify Trade theme.
 *
 * Flow:
 *  1. Read filter config from window.__filterConfig (set by collection-filter-config.liquid)
 *  2. Find existing product cards on page 1 (already rendered by Shopify B2B-correctly)
 *  3. Detect total page count from Shopify's pagination links
 *  4. Fetch remaining pages in parallel, extract their product cards
 *  5. Inject all cards into the grid; hide Shopify's pagination nav
 *  6. Build a filter index from data-filter-* attributes on every card
 *  7. Render the filter sidebar from discovered values
 *  8. On filter change: show/hide cards, update counts, sync URL params
 */

(function () {
  'use strict';

  // ── CONSTANTS ────────────────────────────────────────────────────────────
  const SHOW_MORE_THRESHOLD = 10;
  const DEBOUNCE_MS = 120;
  const CARDS_PER_PAGE = 24;
  const CARD_SELECTOR = '.card-wrapper[data-product-id]';
  const GRID_SELECTOR = '.product-grid, .collection-product-grid, ul.product-grid';
  const PAGINATION_SELECTOR = '.pagination, nav[role="navigation"].pagination__nav, .pagination__nav';

  // ── STATE ─────────────────────────────────────────────────────────────────
  let allCards = [];
  let filterIndex = {};
  let activeFilters = {};
  let activeSort = 'title-asc';
  let collectionSearchQuery = '';
  let currentPage = 1;
  let config = null;

  // ── INIT ──────────────────────────────────────────────────────────────────
  function init() {
    config = window.__filterConfig;
    if (!config || !Array.isArray(config.filters)) {
      console.warn('[CF] No filter config found. Skipping filter init.');
      return;
    }

    readURLState();
    showGridLoading(true);

    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) {
      console.warn('[CF] Product grid not found.');
      showGridLoading(false);
      return;
    }

    // Move grid inside cf-grid-wrapper
    const wrapper = document.getElementById('cf-grid-wrapper');
    if (wrapper && !wrapper.contains(grid)) {
      wrapper.appendChild(grid);
    }

    hidePagination();

    // Grab page-1 cards and remove empty <li> placeholders
    const page1Cards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    allCards = page1Cards;

    Array.from(grid.children).forEach(child => {
      if (!child.matches(CARD_SELECTOR)) child.remove();
    });

    const totalPages = detectTotalPages();

    if (totalPages <= 1) {
      finishInit(grid);
    } else {
      fetchRemainingPages(totalPages, grid).then(() => finishInit(grid));
    }
  }

  function finishInit(grid) {
    buildFilterIndex();
    renderSidebar();
    applyFilters(false);
    showGridLoading(false);
    hideSkeleton();

    // Remove grid skeleton and reveal toolbar
    const gridSkeleton = document.getElementById('cf-grid-skeleton');
    const toolbarSkeleton = document.getElementById('cf-toolbar-skeleton');
    if (gridSkeleton) gridSkeleton.remove();
    if (toolbarSkeleton) toolbarSkeleton.remove();

    const searchWrapper = document.getElementById('cf-search-wrapper');
    const toolbarRow = document.getElementById('cf-toolbar-row');
    if (searchWrapper) searchWrapper.classList.remove('cf-toolbar-hidden');
    if (toolbarRow) toolbarRow.classList.remove('cf-toolbar-hidden');

    bindGlobalEvents();
    }

  // ── PAGE DETECTION & FETCHING ────────────────────────────────────────────
  function detectTotalPages() {
    const pagination = document.querySelector(PAGINATION_SELECTOR);
    if (!pagination) return 1;

    const links = Array.from(pagination.querySelectorAll('a[href*="page="]'));
    let max = 1;
    links.forEach(link => {
      const match = link.href.match(/[?&]page=(\d+)/);
      if (match) max = Math.max(max, parseInt(match[1], 10));
    });
    return max;
  }

  async function fetchRemainingPages(totalPages, grid) {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    const fetches = [];
    for (let p = 2; p <= totalPages; p++) {
      params.set('page', p);
      fetches.push(fetch(`${baseUrl}?${params.toString()}`, { credentials: 'same-origin' }));
    }

    const responses = await Promise.all(fetches);
    const htmlTexts = await Promise.all(responses.map(r => r.text()));

    const parser = new DOMParser();
    htmlTexts.forEach(html => {
      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll(CARD_SELECTOR);
      cards.forEach(card => {
        const imported = document.importNode(card, true);
        grid.appendChild(imported);
        allCards.push(imported);
      });
    });
  }

  function hidePagination() {
    const pagination = document.querySelector(PAGINATION_SELECTOR);
    if (pagination) pagination.style.display = 'none';
  }

  // ── FILTER INDEX ─────────────────────────────────────────────────────────
  function buildFilterIndex() {
    filterIndex = {};

    config.filters.forEach(filter => {
      filterIndex[filter.key] = {};
    });

    allCards.forEach(card => {
      config.filters.forEach(filter => {
        const attrName = config.dataAttribute(filter.key);
        const raw = card.getAttribute(attrName) || '';

        // Collections use "handle|Title" pairs — index by handle, store title for display
        if (filter.key === 'collections') {
          const pairs = parseCollectionPairs(raw);
          pairs.forEach(({ handle, title }) => {
            if (!handle) return;
            if (!filterIndex['collections'][handle]) {
              filterIndex['collections'][handle] = { cards: [], title };
            }
            filterIndex['collections'][handle].cards.push(card);
          });
          return;
        }

        const values = parseMetafieldValue(raw);
        values.forEach(val => {
          if (!val) return;
          if (!filterIndex[filter.key][val]) {
            filterIndex[filter.key][val] = [];
          }
          filterIndex[filter.key][val].push(card);
        });
      });
    });
  }

  /**
   * Parse a raw metafield string into an array of trimmed values.
   * Handles JSON arrays, newline lists, comma lists, plain strings.
   */
  function parseMetafieldValue(raw) {
    if (!raw || !raw.trim()) return [];

    if (raw.trimStart().startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map(v => String(v).trim()).filter(Boolean);
        }
      } catch (_) { /* fall through */ }
    }

    if (raw.includes('\n')) {
      return raw.split('\n').map(v => v.trim()).filter(Boolean);
    }

    if (raw.includes(',')) {
      return raw.split(',').map(v => v.trim()).filter(Boolean);
    }

    return [raw.trim()];
  }

  /**
   * Parse the "handle|Title,handle2|Title2" format used for collections.
   * Returns [{ handle, title }, ...]
   */
  function parseCollectionPairs(raw) {
    if (!raw || !raw.trim()) return [];
    return raw.split(',')
      .filter(pair => pair.includes('|'))
      .map(pair => {
        const sep = pair.indexOf('|');
        return {
          handle: pair.slice(0, sep).trim(),
          title:  pair.slice(sep + 1).trim()
        };
      })
      .filter(p => p.handle);
  }

  // ── SIDEBAR RENDERING ────────────────────────────────────────────────────
  function renderSidebar() {
    const container = document.getElementById('cf-filter-groups');
    if (!container) return;

    hideSkeleton();

    const existing = container.querySelectorAll('.cf-filter-group');
    existing.forEach(el => el.remove());

    config.filters.forEach(filter => {
      // Collections index stores { handle: { cards, title } } — keys are handles
      const values = Object.keys(filterIndex[filter.key] || {});
      if (values.length === 0) return;

      if (filter.type === 'range') {
        renderRangeGroup(container, filter, values);
      } else {
        renderCheckboxGroup(container, filter, values);
      }
    });

    renderDesktopSort();
  }

  function renderCheckboxGroup(container, filter, values) {
    // For collections, sort by display title; otherwise numeric then alphabetic
    const sorted = values.slice().sort((a, b) => {
      if (filter.key === 'collections') {
        const titleA = (filterIndex['collections'][a] || {}).title || a;
        const titleB = (filterIndex['collections'][b] || {}).title || b;
        return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
      }
      const na = parseFloat(a), nb = parseFloat(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });

    const isActive = activeFilters[filter.key] && activeFilters[filter.key].size > 0;
    const isOpen = isActive;

    const group = document.createElement('div');
    group.className = 'cf-filter-group';
    group.dataset.filterKey = filter.key;

    const needsShowMore = sorted.length > SHOW_MORE_THRESHOLD;
    const visibleValues = needsShowMore ? sorted.slice(0, SHOW_MORE_THRESHOLD) : sorted;
    const hiddenValues  = needsShowMore ? sorted.slice(SHOW_MORE_THRESHOLD) : [];

    group.innerHTML = `
      <button
        class="cf-group-trigger"
        aria-expanded="${isOpen}"
        aria-controls="cf-group-body-${filter.key}"
        type="button">
        <span>${escHtml(filter.label)}</span>
        ${isActive ? '<span class="cf-group-active-dot" aria-hidden="true"></span>' : ''}
        <svg class="cf-group-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div
        class="cf-group-body ${isOpen ? 'is-open' : ''}"
        id="cf-group-body-${filter.key}"
        ${isOpen ? '' : 'hidden'}>
        ${sorted.length > 10 ? `<input class="cf-option-search" type="search" placeholder="Search ${escHtml(filter.label)}…" aria-label="Search ${escHtml(filter.label)} options">` : ''}
        <ul class="cf-options-list" role="list">
          ${visibleValues.map(val => renderCheckboxOption(filter.key, val)).join('')}
          ${hiddenValues.map(val => renderCheckboxOption(filter.key, val, true)).join('')}
        </ul>
        ${needsShowMore ? `<button class="cf-show-more" data-filter-key="${escAttr(filter.key)}" type="button">Show ${hiddenValues.length} more</button>` : ''}
      </div>
    `;

    container.appendChild(group);

    const trigger = group.querySelector('.cf-group-trigger');
    const body    = group.querySelector('.cf-group-body');
    trigger.addEventListener('click', () => toggleGroup(trigger, body));

    const search = group.querySelector('.cf-option-search');
    if (search) search.addEventListener('input', debounce(e => filterOptions(e.target, group), DEBOUNCE_MS));

    const showMore = group.querySelector('.cf-show-more');
    if (showMore) showMore.addEventListener('click', () => expandGroup(group, showMore));
  }

  function renderCheckboxOption(filterKey, val, hidden = false) {
    const checked = activeFilters[filterKey] && activeFilters[filterKey].has(val) ? 'checked' : '';

    // Collections: val is the handle, display label is the title stored in the index
    const isCollections = filterKey === 'collections';
    const entry = isCollections ? filterIndex['collections'][val] : null;
    const displayLabel = entry ? entry.title : val;
    const count = isCollections
      ? (entry ? entry.cards.length : 0)
      : (filterIndex[filterKey][val] || []).length;

    return `
      <li class="cf-option" ${hidden ? 'data-hidden="true" style="display:none"' : ''}>
        <label class="cf-option-label">
          <input
            class="cf-option-checkbox"
            type="checkbox"
            data-filter-key="${escAttr(filterKey)}"
            data-filter-value="${escAttr(val)}"
            ${checked}
            aria-label="${escAttr(displayLabel)} (${count} products)">
          <span class="cf-option-name">${escHtml(displayLabel)}</span>
          <span class="cf-option-count">${count}</span>
        </label>
      </li>
    `;
  }

  function renderRangeGroup(container, filter, values) {
    const nums = values.map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (nums.length === 0) return;

    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const step = detectStep(nums);

    const activeMin = activeFilters[filter.key + '__min'] !== undefined
      ? activeFilters[filter.key + '__min'] : min;
    const activeMax = activeFilters[filter.key + '__max'] !== undefined
      ? activeFilters[filter.key + '__max'] : max;

    const isActive = activeMin !== min || activeMax !== max;
    const isOpen = isActive;

    const group = document.createElement('div');
    group.className = 'cf-filter-group';
    group.dataset.filterKey = filter.key;
    group.dataset.filterType = 'range';

    group.innerHTML = `
      <button
        class="cf-group-trigger"
        aria-expanded="${isOpen}"
        aria-controls="cf-group-body-${filter.key}"
        type="button">
        <span>${escHtml(filter.label)}</span>
        ${isActive ? '<span class="cf-group-active-dot" aria-hidden="true"></span>' : ''}
        <svg class="cf-group-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div
        class="cf-group-body ${isOpen ? 'is-open' : ''}"
        id="cf-group-body-${filter.key}"
        ${isOpen ? '' : 'hidden'}>
        <div class="cf-range-wrapper"
          data-filter-key="${escAttr(filter.key)}"
          data-min="${min}"
          data-max="${max}"
          data-step="${step}">
          <div class="cf-range-track">
            <div class="cf-range-fill" id="cf-range-fill-${filter.key}"></div>
            <input class="cf-range-input" type="range"
              id="cf-range-min-${filter.key}"
              min="${min}" max="${max}" step="${step}"
              value="${activeMin}"
              aria-label="${escAttr(filter.label)} minimum">
            <input class="cf-range-input" type="range"
              id="cf-range-max-${filter.key}"
              min="${min}" max="${max}" step="${step}"
              value="${activeMax}"
              aria-label="${escAttr(filter.label)} maximum">
          </div>
          <div class="cf-range-labels">
            <span id="cf-range-label-min-${filter.key}">${activeMin}</span>
            <span id="cf-range-label-max-${filter.key}">${activeMax}</span>
          </div>
          <div class="cf-range-inputs">
            <div class="cf-range-field">
              <label for="cf-range-num-min-${filter.key}">Min</label>
              <input type="number" id="cf-range-num-min-${filter.key}"
                min="${min}" max="${max}" step="${step}" value="${activeMin}">
            </div>
            <div class="cf-range-field">
              <label for="cf-range-num-max-${filter.key}">Max</label>
              <input type="number" id="cf-range-num-max-${filter.key}"
                min="${min}" max="${max}" step="${step}" value="${activeMax}">
            </div>
          </div>
        </div>
      </div>
    `;

    container.appendChild(group);

    const trigger = group.querySelector('.cf-group-trigger');
    const body    = group.querySelector('.cf-group-body');
    trigger.addEventListener('click', () => toggleGroup(trigger, body));

    bindRangeSlider(group, filter.key, min, max);
    updateRangeFill(filter.key, activeMin, activeMax, min, max);
  }

  function bindRangeSlider(group, key, min, max) {
    const sliderMin = group.querySelector(`#cf-range-min-${key}`);
    const sliderMax = group.querySelector(`#cf-range-max-${key}`);
    const numMin    = group.querySelector(`#cf-range-num-min-${key}`);
    const numMax    = group.querySelector(`#cf-range-num-max-${key}`);

    function onRangeChange() {
      let lo = parseFloat(sliderMin.value);
      let hi = parseFloat(sliderMax.value);
      if (lo > hi) { lo = hi; sliderMin.value = lo; }
      if (hi < lo) { hi = lo; sliderMax.value = hi; }
      numMin.value = lo;
      numMax.value = hi;
      updateRangeLabels(key, lo, hi);
      updateRangeFill(key, lo, hi, min, max);
      setRangeFilter(key, lo, hi, min, max);
    }

    function onNumChange() {
      let lo = parseFloat(numMin.value);
      let hi = parseFloat(numMax.value);
      lo = Math.max(min, Math.min(lo, max));
      hi = Math.max(min, Math.min(hi, max));
      if (lo > hi) lo = hi;
      sliderMin.value = lo;
      sliderMax.value = hi;
      numMin.value = lo;
      numMax.value = hi;
      updateRangeLabels(key, lo, hi);
      updateRangeFill(key, lo, hi, min, max);
      setRangeFilter(key, lo, hi, min, max);
    }

    sliderMin.addEventListener('input', onRangeChange);
    sliderMax.addEventListener('input', onRangeChange);
    numMin.addEventListener('change', debounce(onNumChange, 400));
    numMax.addEventListener('change', debounce(onNumChange, 400));
  }

  function updateRangeLabels(key, lo, hi) {
    const labelMin = document.getElementById(`cf-range-label-min-${key}`);
    const labelMax = document.getElementById(`cf-range-label-max-${key}`);
    if (labelMin) labelMin.textContent = lo;
    if (labelMax) labelMax.textContent = hi;
  }

  function updateRangeFill(key, lo, hi, min, max) {
    const fill = document.getElementById(`cf-range-fill-${key}`);
    if (!fill) return;
    const range = max - min;
    if (range === 0) return;
    const left  = ((lo - min) / range) * 100;
    const right = ((max - hi) / range) * 100;
    fill.style.left  = left  + '%';
    fill.style.right = right + '%';
  }

  function setRangeFilter(key, lo, hi, min, max) {
    if (lo === min && hi === max) {
      delete activeFilters[key + '__min'];
      delete activeFilters[key + '__max'];
    } else {
      activeFilters[key + '__min'] = lo;
      activeFilters[key + '__max'] = hi;
    }
    applyFilters(true);
  }

  function detectStep(nums) {
    if (nums.every(n => Number.isInteger(n))) return 1;
    const maxDecimals = Math.max(...nums.map(n => {
      const s = String(n);
      return s.includes('.') ? s.split('.')[1].length : 0;
    }));
    return Math.pow(10, -maxDecimals);
  }

  function renderDesktopSort() {
    const desktopSort = document.getElementById('cf-sort-select-desktop');
    if (!desktopSort) return;
    desktopSort.addEventListener('change', e => {
        activeSort = e.target.value;
        const mobileSelect = document.getElementById('cf-sort-select');
        if (mobileSelect) mobileSelect.value = activeSort;
        applyFilters(true, true);
    });
    }

  // ── COLLAPSIBLE ──────────────────────────────────────────────────────────
  function toggleGroup(trigger, body) {
    const open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));
    if (!open) {
      body.removeAttribute('hidden');
      requestAnimationFrame(() => body.classList.add('is-open'));
    } else {
      body.classList.remove('is-open');
      setTimeout(() => body.setAttribute('hidden', ''), 250);
    }
  }

  function filterOptions(input, group) {
    const q = input.value.toLowerCase().trim();
    const items = group.querySelectorAll('.cf-option');
    items.forEach(item => {
      const name = item.querySelector('.cf-option-name');
      const match = !q || (name && name.textContent.toLowerCase().includes(q));
      const checkbox = item.querySelector('.cf-option-checkbox');
      const isChecked = checkbox && checkbox.checked;
      item.style.display = (match || isChecked) ? '' : 'none';
    });
  }

  function expandGroup(group, showMoreBtn) {
    const hidden = group.querySelectorAll('.cf-option[data-hidden="true"]');
    hidden.forEach(item => {
      item.removeAttribute('data-hidden');
      item.style.display = '';
    });
    showMoreBtn.remove();
  }

  // ── FILTER APPLICATION ───────────────────────────────────────────────────
  function applyFilters(pushHistory, resetPage = false) {
    if (resetPage) currentPage = 1;
    const activeCheckboxKeys = Object.keys(activeFilters).filter(
      k => !k.endsWith('__min') && !k.endsWith('__max') && activeFilters[k] instanceof Set
    );
    const activeRangeKeys = config.filters
      .filter(f => f.type === 'range')
      .map(f => f.key)
      .filter(k => activeFilters[k + '__min'] !== undefined || activeFilters[k + '__max'] !== undefined);

    let visibleCards = allCards;

    // ── Checkbox filtering (AND between groups, OR within group) ──
    activeCheckboxKeys.forEach(key => {
      if (activeFilters[key].size === 0) return;
      const selectedValues = activeFilters[key];

      if (key === 'collections') {
        visibleCards = visibleCards.filter(card => {
          const attrName = config.dataAttribute('collections');
          const raw = card.getAttribute(attrName) || '';
          const pairs = parseCollectionPairs(raw);
          return pairs.some(p => selectedValues.has(p.handle));
        });
        return;
      }

      visibleCards = visibleCards.filter(card => {
        const attrName = config.dataAttribute(key);
        const raw = card.getAttribute(attrName) || '';
        const cardValues = parseMetafieldValue(raw);
        return cardValues.some(v => selectedValues.has(v));
      });
    });

    // ── Range filtering ──
    activeRangeKeys.forEach(key => {
      const lo = activeFilters[key + '__min'];
      const hi = activeFilters[key + '__max'];
      const attrName = config.dataAttribute(key);
      visibleCards = visibleCards.filter(card => {
        const raw = card.getAttribute(attrName) || '';
        const n = parseFloat(raw);
        if (isNaN(n)) return false;
        return n >= lo && n <= hi;
      });
    });

    // ── Collection search (title, SKU, alternate SKU) ──
    if (collectionSearchQuery) {
      visibleCards = visibleCards.filter(card => {
        if (getCardTitle(card).includes(collectionSearchQuery)) return true;
        const sku = card.getAttribute('data-product-sku') || '';
        if (sku.toLowerCase().includes(collectionSearchQuery)) return true;
        const cpn = card.getAttribute('data-cpn') || '';
        const cpnValues = parseMetafieldValue(cpn);
        if (cpnValues.some(v => v.toLowerCase().includes(collectionSearchQuery))) return true;
        return false;
      });
    }

    // ── Sorting ──
    visibleCards = sortCards(visibleCards, activeSort);

    // ── Pagination ──
    const totalCards = visibleCards.length;
    const totalPages = Math.max(1, Math.ceil(totalCards / CARDS_PER_PAGE));
    if (currentPage > totalPages) currentPage = 1;
    const pageStart = (currentPage - 1) * CARDS_PER_PAGE;
    const pageEnd = pageStart + CARDS_PER_PAGE;
    const pageCards = visibleCards.slice(pageStart, pageEnd);
    const pageSet = new Set(pageCards);

    // ── Show / hide ──
    allCards.forEach(card => {
      card.style.display = pageSet.has(card) ? '' : 'none';
    });

    // Reorder visible cards in DOM
    const grid = document.querySelector(GRID_SELECTOR);
    if (grid) {
      pageCards.forEach(card => grid.appendChild(card));
    }

    // ── Update results count ──
    const meta = document.getElementById('cf-results-meta');
    if (meta) {
      const from = totalCards === 0 ? 0 : pageStart + 1;
      const to = Math.min(pageEnd, totalCards);
      meta.innerHTML = totalCards === 0
    ? '<strong>0</strong> products'
    : `<strong>${from}–${to}</strong> of <strong>${totalCards}</strong> product${totalCards === 1 ? '' : 's'}`;
    }

    // ── Empty state ──
    const emptyState = document.getElementById('cf-empty-state');
    if (emptyState) emptyState.hidden = totalCards > 0;

    // ── Render pagination ──
    renderPagination(totalCards, totalPages);

    // ── Update chips ──
    renderChips();

    // ── Update sidebar counts ──
    updateSidebarCounts(visibleCards);

    // ── URL sync ──
    if (pushHistory) syncURL();
  }

  function renderPagination(totalCards, totalPages) {
    let container = document.getElementById('cf-pagination');
    if (!container) {
      container = document.createElement('nav');
      container.id = 'cf-pagination';
      container.className = 'cf-pagination';
      container.setAttribute('aria-label', 'Product pagination');
      const gridWrapper = document.getElementById('cf-grid-wrapper');
      if (gridWrapper) gridWrapper.appendChild(container);
    }

    if (totalPages <= 1) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    // Build page numbers with ellipsis
    const pages = [];
    const delta = 2; // pages either side of current
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pages.push(i);
      } else if (
        i === currentPage - delta - 1 ||
        i === currentPage + delta + 1
      ) {
        pages.push('...');
      }
    }

    container.innerHTML = `
      <button class="cf-page-btn cf-page-prev" ${currentPage === 1 ? 'disabled' : ''} aria-label="Previous page">
        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
          <path d="M7 1L2 6l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      ${pages.map(p => p === '...'
        ? `<span class="cf-page-ellipsis">…</span>`
        : `<button class="cf-page-btn ${p === currentPage ? 'is-active' : ''}" data-page="${p}" aria-label="Page ${p}" ${p === currentPage ? 'aria-current="page"' : ''}>${p}</button>`
      ).join('')}
      <button class="cf-page-btn cf-page-next" ${currentPage === totalPages ? 'disabled' : ''} aria-label="Next page">
        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
          <path d="M1 1l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    // Bind clicks
    container.querySelector('.cf-page-prev').addEventListener('click', () => {
      if (currentPage > 1) { currentPage--; applyFilters(true); scrollToGrid(); }
    });
    container.querySelector('.cf-page-next').addEventListener('click', () => {
      if (currentPage < totalPages) { currentPage++; applyFilters(true); scrollToGrid(); }
    });
    container.querySelectorAll('.cf-page-btn[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        currentPage = parseInt(btn.dataset.page, 10);
        applyFilters(true);
        scrollToGrid();
      });
    });
  }

  function scrollToGrid() {
    const wrapper = document.getElementById('cf-grid-wrapper');
    if (wrapper) {
      const top = wrapper.getBoundingClientRect().top + window.scrollY - 20;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  function sortCards(cards, sortKey) {
    return cards.slice().sort((a, b) => {
      switch (sortKey) {
        case 'title-asc':  return getCardTitle(a).localeCompare(getCardTitle(b));
        case 'title-desc': return getCardTitle(b).localeCompare(getCardTitle(a));
        case 'price-asc':  return getCardPrice(a) - getCardPrice(b);
        case 'price-desc': return getCardPrice(b) - getCardPrice(a);
        case 'created-desc': return (getCardCreated(b) || 0) - (getCardCreated(a) || 0);
        default: return 0;
      }
    });
  }

  function getCardTitle(card) {
    const el = card.querySelector('.card__heading, .card-information__text h3, h3, h2');
    return el ? el.textContent.trim().toLowerCase() : '';
  }

  function getCardPrice(card) {
    const el = card.querySelector('.price__regular .price-item, .price .price-item--regular, [data-price]');
    if (!el) return 0;
    const text = el.textContent.replace(/[^0-9.]/g, '');
    return parseFloat(text) || 0;
  }

  function getCardCreated(card) {
    const ts = card.getAttribute('data-created-at');
    return ts ? new Date(ts).getTime() : null;
  }

  // ── DYNAMIC COUNTS ───────────────────────────────────────────────────────
  function updateSidebarCounts(visibleCards) {
    const visibleSet = new Set(visibleCards);

    config.filters.forEach(filter => {
      if (filter.type === 'range') return;

      const options = document.querySelectorAll(
        `.cf-option-checkbox[data-filter-key="${CSS.escape(filter.key)}"]`
      );

      options.forEach(checkbox => {
        const val = checkbox.getAttribute('data-filter-value');
        const isChecked = checkbox.checked;

        // Count against all cards matching every OTHER active filter group
        // so sibling options within the same group show accurate counts
        const otherFiltersCards = allCards.filter(card => {
          return config.filters.every(f => {
            if (f.key === filter.key) return true; // skip current group
            if (f.type === 'range') {
              const lo = activeFilters[f.key + '__min'];
              const hi = activeFilters[f.key + '__max'];
              if (lo === undefined && hi === undefined) return true;
              const raw = card.getAttribute(config.dataAttribute(f.key)) || '';
              const n = parseFloat(raw);
              return !isNaN(n) && n >= lo && n <= hi;
            }
            const selected = activeFilters[f.key];
            if (!selected || selected.size === 0) return true;
            if (f.key === 'collections') {
              const pairs = parseCollectionPairs(card.getAttribute(config.dataAttribute('collections')) || '');
              return pairs.some(p => selected.has(p.handle));
            }
            const cardValues = parseMetafieldValue(card.getAttribute(config.dataAttribute(f.key)) || '');
            return cardValues.some(v => selected.has(v));
          });
        });

        const entry = filter.key === 'collections'
          ? filterIndex['collections'][val]
          : null;
        const cardsForValue = entry
          ? entry.cards
          : (filterIndex[filter.key][val] || []);

        const count = cardsForValue.filter(card => otherFiltersCards.includes(card)).length;

        const li = checkbox.closest('.cf-option');
        const countEl = li ? li.querySelector('.cf-option-count') : null;
        if (countEl) countEl.textContent = count;

        if (li) {
          li.classList.toggle('cf-option--zero', count === 0 && !isChecked);
        }
      });
    });
  }

  // ── ACTIVE FILTER CHIPS ──────────────────────────────────────────────────
  function renderChips() {
    const chipsList = document.getElementById('cf-chips-list');
    const chipsArea = document.getElementById('cf-active-chips');
    const activeCountBadge = document.getElementById('cf-active-count');
    if (!chipsList || !chipsArea) return;

    chipsList.innerHTML = '';
    let totalActive = 0;

    // Checkbox chips
    Object.entries(activeFilters).forEach(([key, val]) => {
      if (!(val instanceof Set)) return;
      val.forEach(v => {
        totalActive++;
        const filter = config.filters.find(f => f.key === key);
        const label = filter ? filter.label : key;

        // For collections, display the title not the handle
        const isCollections = key === 'collections';
        const entry = isCollections ? filterIndex['collections'][v] : null;
        const displayValue = entry ? entry.title : v;

        const li = document.createElement('li');
        li.innerHTML = `
          <span class="cf-chip">
            <span class="cf-chip-label">${escHtml(label)}: ${escHtml(displayValue)}</span>
            <button class="cf-chip-remove" type="button" aria-label="Remove filter: ${escAttr(label)}: ${escAttr(displayValue)}">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </span>
        `;
        li.querySelector('.cf-chip-remove').addEventListener('click', () => {
          activeFilters[key].delete(v);
          const cb = document.querySelector(
            `.cf-option-checkbox[data-filter-key="${CSS.escape(key)}"][data-filter-value="${CSS.escape(v)}"]`
          );
          if (cb) cb.checked = false;
          applyFilters(true);
          updateGroupActiveDot(key);
        });
        chipsList.appendChild(li);
      });
    });

    // Range chips
    config.filters.filter(f => f.type === 'range').forEach(filter => {
      const lo = activeFilters[filter.key + '__min'];
      const hi = activeFilters[filter.key + '__max'];
      if (lo === undefined && hi === undefined) return;
      totalActive++;
      const li = document.createElement('li');
      li.innerHTML = `
        <span class="cf-chip">
          <span class="cf-chip-label">${escHtml(filter.label)}: ${lo} – ${hi}</span>
          <button class="cf-chip-remove" type="button" aria-label="Remove ${escAttr(filter.label)} range filter">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </span>
      `;
      li.querySelector('.cf-chip-remove').addEventListener('click', () => {
        delete activeFilters[filter.key + '__min'];
        delete activeFilters[filter.key + '__max'];
        const wrapper = document.querySelector(`.cf-range-wrapper[data-filter-key="${CSS.escape(filter.key)}"]`);
        if (wrapper) {
          const min = parseFloat(wrapper.dataset.min);
          const max = parseFloat(wrapper.dataset.max);
          const sliderMin = document.getElementById(`cf-range-min-${filter.key}`);
          const sliderMax = document.getElementById(`cf-range-max-${filter.key}`);
          if (sliderMin) sliderMin.value = min;
          if (sliderMax) sliderMax.value = max;
          updateRangeLabels(filter.key, min, max);
          updateRangeFill(filter.key, min, max, min, max);
        }
        applyFilters(true);
        updateGroupActiveDot(filter.key);
      });
      chipsList.appendChild(li);
    });

    chipsArea.hidden = totalActive === 0;
    if (activeCountBadge) {
      activeCountBadge.hidden = totalActive === 0;
      activeCountBadge.textContent = totalActive;
    }
  }

  function updateGroupActiveDot(key) {
    const group = document.querySelector(`.cf-filter-group[data-filter-key="${CSS.escape(key)}"]`);
    if (!group) return;
    const dot = group.querySelector('.cf-group-active-dot');
    const isActive = (activeFilters[key] instanceof Set && activeFilters[key].size > 0)
      || activeFilters[key + '__min'] !== undefined;
    if (isActive && !dot) {
      const trigger = group.querySelector('.cf-group-trigger');
      const newDot = document.createElement('span');
      newDot.className = 'cf-group-active-dot';
      newDot.setAttribute('aria-hidden', 'true');
      trigger.insertBefore(newDot, trigger.querySelector('.cf-group-chevron'));
    } else if (!isActive && dot) {
      dot.remove();
    }
  }

  // ── GLOBAL EVENT BINDING ─────────────────────────────────────────────────
  function bindGlobalEvents() {
    // Checkbox changes (delegated)
    const filterGroups = document.getElementById('cf-filter-groups');
    if (filterGroups) {
      filterGroups.addEventListener('change', e => {
        const cb = e.target.closest('.cf-option-checkbox');
        if (!cb) return;
        const key = cb.getAttribute('data-filter-key');
        const val = cb.getAttribute('data-filter-value');
        if (!activeFilters[key]) activeFilters[key] = new Set();
        if (cb.checked) {
          activeFilters[key].add(val);
        } else {
          activeFilters[key].delete(val);
        }
        applyFilters(true, true);
        updateGroupActiveDot(key);
      });
    }

    // Clear all
    const clearAll = document.getElementById('cf-clear-all');
    if (clearAll) clearAll.addEventListener('click', clearAllFilters);

    const emptyClear = document.getElementById('cf-empty-clear');
    if (emptyClear) emptyClear.addEventListener('click', clearAllFilters);

    // Mobile drawer
    const trigger  = document.getElementById('cf-mobile-trigger');
    const sidebar  = document.getElementById('cf-sidebar');
    const closeBtn = document.getElementById('cf-sidebar-close');
    const backdrop = document.getElementById('cf-backdrop');
    const applyBtn = document.getElementById('cf-apply-mobile');

    if (trigger && sidebar) {
      trigger.addEventListener('click', () => openDrawer(sidebar, trigger, backdrop));
      closeBtn && closeBtn.addEventListener('click', () => closeDrawer(sidebar, trigger, backdrop));
      backdrop && backdrop.addEventListener('click', () => closeDrawer(sidebar, trigger, backdrop));
      applyBtn && applyBtn.addEventListener('click', () => closeDrawer(sidebar, trigger, backdrop));

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && sidebar.classList.contains('is-open')) {
          closeDrawer(sidebar, trigger, backdrop);
        }
      });
    }

    // Mobile sort
    const mobileSort = document.getElementById('cf-sort-select');
    if (mobileSort) {
      mobileSort.addEventListener('change', e => {
        activeSort = e.target.value;
        const desktopSort = document.getElementById('cf-sort-select-desktop');
        if (desktopSort) desktopSort.value = activeSort;
        applyFilters(true);
      });
    }

    // Collection search bar
    bindCollectionSearch();

    // Browser back/forward
    window.addEventListener('popstate', () => {
      readURLState();
      syncCheckboxesToState();
      applyFilters(false);
    });
  }

  function clearAllFilters() {
    activeFilters = {};
    collectionSearchQuery = '';
    const searchInput = document.getElementById('cf-collection-search');
    const searchClear = document.getElementById('cf-collection-search-clear');
    if (searchInput) searchInput.value = '';
    if (searchClear) searchClear.hidden = true;
    document.querySelectorAll('.cf-option-checkbox').forEach(cb => cb.checked = false);
    config.filters.filter(f => f.type === 'range').forEach(filter => {
      const wrapper = document.querySelector(`.cf-range-wrapper[data-filter-key="${CSS.escape(filter.key)}"]`);
      if (!wrapper) return;
      const min = parseFloat(wrapper.dataset.min);
      const max = parseFloat(wrapper.dataset.max);
      const sliderMin = document.getElementById(`cf-range-min-${filter.key}`);
      const sliderMax = document.getElementById(`cf-range-max-${filter.key}`);
      const numMin    = document.getElementById(`cf-range-num-min-${filter.key}`);
      const numMax    = document.getElementById(`cf-range-num-max-${filter.key}`);
      if (sliderMin) sliderMin.value = min;
      if (sliderMax) sliderMax.value = max;
      if (numMin) numMin.value = min;
      if (numMax) numMax.value = max;
      updateRangeLabels(filter.key, min, max);
      updateRangeFill(filter.key, min, max, min, max);
    });
    document.querySelectorAll('.cf-group-active-dot').forEach(d => d.remove());
    applyFilters(true);
  }

  // ── COLLECTION SEARCH BAR ────────────────────────────────────────────────
  function bindCollectionSearch() {
    const input = document.getElementById('cf-collection-search');
    const clearBtn = document.getElementById('cf-collection-search-clear');
    if (!input) return;

    input.addEventListener('input', debounce(function() {
      collectionSearchQuery = input.value.trim().toLowerCase();
      if (clearBtn) clearBtn.hidden = collectionSearchQuery === '';
      applyFilters(true, true);
    }, DEBOUNCE_MS));

    clearBtn && clearBtn.addEventListener('click', function() {
      input.value = '';
      collectionSearchQuery = '';
      clearBtn.hidden = true;
      applyFilters(true, true);
      input.focus();
    });
  }

  // ── MOBILE DRAWER ────────────────────────────────────────────────────────
  function openDrawer(sidebar, trigger, backdrop) {
    sidebar.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    if (backdrop) {
      backdrop.style.display = 'block';
      requestAnimationFrame(() => backdrop.classList.add('is-visible'));
    }
    document.body.style.overflow = 'hidden';
    const firstFocusable = sidebar.querySelector('button, input, select, [tabindex]');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeDrawer(sidebar, trigger, backdrop) {
    sidebar.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    if (backdrop) {
      backdrop.classList.remove('is-visible');
      backdrop.addEventListener('transitionend', () => { backdrop.style.display = ''; }, { once: true });
    }
    document.body.style.overflow = '';
    trigger.focus();
  }

  // ── URL SYNC ─────────────────────────────────────────────────────────────
  function syncURL() {
    const params = new URLSearchParams();

    Object.entries(activeFilters).forEach(([key, val]) => {
      if (val instanceof Set) {
        val.forEach(v => params.append(`cf_${key}`, v));
      } else if (typeof val === 'number') {
        params.set(`cf_${key}`, val);
      }
    });

    if (collectionSearchQuery) {
      params.set('cf_search', collectionSearchQuery);
    }

    if (activeSort && activeSort !== 'title-asc') {
      params.set('cf_sort', activeSort);
    }

    if (currentPage > 1) {
      params.set('cf_pg', currentPage);
    }

    const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.pushState({ cf: true }, '', newUrl);
  }

  function readURLState() {
    activeFilters = {};
    const params = new URLSearchParams(window.location.search);

    params.forEach((val, key) => {
      if (!key.startsWith('cf_')) return;
      const filterKey = key.slice(3);

      if (filterKey === 'sort') { activeSort = val; return; }
      if (filterKey === 'search') { collectionSearchQuery = val; return; }
      if (filterKey === 'pg') { currentPage = Math.max(1, parseInt(val, 10)); return; }

      if (filterKey.endsWith('__min') || filterKey.endsWith('__max')) {
        activeFilters[filterKey] = parseFloat(val);
        return;
      }

      if (!activeFilters[filterKey]) activeFilters[filterKey] = new Set();
      activeFilters[filterKey].add(val);
    });
  }

  function syncCheckboxesToState() {
    document.querySelectorAll('.cf-option-checkbox').forEach(cb => {
      const key = cb.getAttribute('data-filter-key');
      const val = cb.getAttribute('data-filter-value');
      cb.checked = !!(activeFilters[key] && activeFilters[key].has(val));
    });
  }

  // ── LOADING / SKELETON ───────────────────────────────────────────────────
  function showGridLoading(show) {
    const el = document.getElementById('cf-grid-loading');
    if (el) el.classList.toggle('is-hidden', !show);
  }

  function hideSkeleton() {
    const sk = document.getElementById('cf-skeleton');
    if (sk) sk.remove();
  }

  // ── UTILITIES ────────────────────────────────────────────────────────────
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // ── BOOT ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
